import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import LocaleLink from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';
import { TRUST_EN, type TrustIcon, type TrustPillar } from '@/copy/trust-en';
import { TRUST_AR } from '@/copy/trust-ar';
import './TrustPage.css';
import './TrustTabs.css';

/* ── Comparison variant of the Trust page: the four Pri pillars are organised
   into horizontal tabs (one pillar visible at a time) instead of stacked
   cards, so the page breathes. Same content, same TRUST_EN source. ── */

/* Layout effects cannot run on the server; useEffect there keeps React quiet
 * during the build-time renderToString without moving the browser measurement
 * after paint (which would make the indicator visibly jump — see the placement
 * comment on the effect below).
 * NOTE: react-hooks/exhaustive-deps matches hooks by NAME, so the dependency
 * array of the effect using this is no longer linted. Keep it in sync by
 * hand. [pass2-NIT2] */
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function Icon({ name }: { name: TrustIcon }) {
  const c = {
    width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
  };
  switch (name) {
    case 'shield-check':
      return (<svg {...c}><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /><path d="M9 12l2 2 4-4" /></svg>);
    case 'no-ai':
      return (<svg {...c}><rect x="4" y="6" width="16" height="12" rx="2.5" /><path d="M9 10v4M15 10v4M9.5 12h5" /><path d="M4 4l16 16" /></svg>);
    case 'lock':
      return (<svg {...c}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /><path d="M12 15v2" /></svg>);
    case 'star':
      return (<svg {...c}><path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z" /></svg>);
    case 'pin':
      return (<svg {...c}><path d="M12 21s7-5.2 7-11a7 7 0 0 0-14 0c0 5.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>);
    case 'doc':
      return (<svg {...c}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13l1.8 1.8L14 11.5" /></svg>);
  }
}

function Arrow() {
  return (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>);
}
function Chevron() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>);
}
/* `pri` is the teal leading segment, stated per pillar in the copy. English
 * pillars all share "Pri" (the acrostic the page is built on); Arabic has no
 * such prefix, so it omits `pri` and the word renders in one colour. */
function PriWord({ word, pri }: { word: string; pri?: string }) {
  if (!pri || !word.startsWith(pri)) return <>{word}</>;
  return (<><span className="trust-pri">{pri}</span>{word.slice(pri.length)}</>);
}
function BrandTitle({ text, brand }: { text: string; brand: { pri: string; rest: string } }) {
  const token = `${brand.pri}${brand.rest}`;
  const idx = text.indexOf(token);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="trust-pri">{brand.pri}</span>
      {brand.rest}
      {text.slice(idx + token.length)}
    </>
  );
}

function Panel({ p }: { p: TrustPillar }) {
  return (
    <div className="trust-panel" role="tabpanel" id={`panel-${p.id}`} aria-labelledby={`tab-${p.id}`}>
      <div className="trust-panel__head">
        <span className="trust-panel__icon">
          <Icon name={p.icon} />
        </span>
        <div>
          <h3 className="trust-panel__word"><PriWord word={p.word} pri={p.pri} /></h3>
          <p className="trust-panel__subtitle">{p.subtitle}</p>
        </div>
      </div>

      <p className="trust-panel__summary">{p.summary}</p>

      <div className="trust-panel__body">
        {p.groups.map((g, gi) => (
          <div key={gi} className="trust-group">
            {g.subhead && <p className="trust-group__subhead">{g.subhead}</p>}
            <ul className="trust-points">
              {g.points.map((pt) => (
                <li key={pt.label} className="trust-points__item">
                  <span className="trust-points__tick" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </span>
                  <span className="trust-points__text"><strong>{pt.label}</strong> {pt.body}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {p.linkTo && p.linkLabel && (
        <LocaleLink to={p.linkTo} className="trust-pillar__link">
          {p.linkLabel}
          <Arrow />
        </LocaleLink>
      )}
    </div>
  );
}

export default function TrustPageTabs() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const doc = isAr ? TRUST_AR : TRUST_EN;
  const dir = isAr ? 'rtl' : 'ltr';
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<CSSProperties>({});

  /* Slide the active-tab underline. Works unchanged in RTL because the bar is
   * anchored with physical `left: 0` rather than inset-inline-start, so this
   * offsetLeft-based transform shares its coordinate system. See the comment
   * on .trust-tabs__indicator in TrustTabs.css before changing either half.
   *
   * Measuring once on mount is not enough. The tab strip reflows after the
   * webfont lands, which stranded the bar 5px off its tab on /ar/trust: the
   * Arabic face is fetched only once Arabic glyphs are in the DOM (the
   * @fontsource unicode-range split), so it always arrives after first paint.
   * The same drift exists in English on any resize that re-centres the strip.
   * Re-measure on font load and whenever the strip or the active tab changes
   * size. */
  useIsoLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const place = () => {
      const btn = list.children[active];
      if (!(btn instanceof HTMLElement)) return;
      setIndicator({ transform: `translateX(${btn.offsetLeft}px)`, width: `${btn.offsetWidth}px` });
    };
    place();

    const ro = new ResizeObserver(place);
    ro.observe(list);
    const btn = list.children[active];
    if (btn instanceof HTMLElement) ro.observe(btn);

    let live = true;
    void document.fonts?.ready.then(() => { if (live) place(); });

    return () => { live = false; ro.disconnect(); };
  }, [active]);

  /* Arrow keys follow the visual order, so they invert under RTL: ArrowRight
   * moves to the tab drawn to the right, which is the PREVIOUS one. */
  const onKey = (e: React.KeyboardEvent) => {
    const n = doc.pillars.length;
    const step = (d: number) => { e.preventDefault(); setActive((i) => (i + d + n) % n); };
    if (e.key === 'ArrowRight') step(isAr ? -1 : 1);
    if (e.key === 'ArrowLeft') step(isAr ? 1 : -1);
  };

  return (
    <div className="trust-page" dir={dir}>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="trust-hero">
        <div className="mkt-container trust-hero__inner">
          <div className="trust-hero__lede">
            <p className="mkt-eyebrow" style={{ margin: 0 }}>{doc.eyebrow}</p>
            <h1 className="trust-hero__title">{doc.title}</h1>
            <p className="trust-hero__lead">{doc.lead}</p>
          </div>

          <div className="trust-reg" role="group" aria-label={doc.a11y.reg}>
            <div className="trust-reg__logos">
              <img src="/trust/sdaia.svg" alt={doc.reg.logoAlt.sdaia} className="trust-reg__logo trust-reg__logo--sdaia" />
              <span className="trust-reg__divider" aria-hidden="true" />
              <img src="/trust/ndgp.svg" alt={doc.reg.logoAlt.ndgp} className="trust-reg__logo trust-reg__logo--ndgp" />
            </div>
            <p className="trust-reg__heading">{doc.reg.heading}</p>
            <dl className="trust-reg__facts">
              <div className="trust-reg__fact"><dt>{doc.reg.regLabel}</dt><dd className="trust-reg__regno">{doc.reg.regNo}</dd></div>
              <div className="trust-reg__fact"><dt>{doc.reg.statusLabel}</dt><dd><span className="trust-reg__status"><span className="trust-reg__status-dot" aria-hidden="true" />{doc.reg.status}</span></dd></div>
              <div className="trust-reg__fact"><dt>{doc.reg.issuedLabel}</dt><dd>{doc.reg.issued}</dd></div>
            </dl>
            <p className="trust-reg__platform">{doc.reg.platform}</p>
            <div className="trust-reg__verify">
              <a className="trust-reg__qr" href={doc.reg.verifyUrl} target="_blank" rel="noopener noreferrer">
                <img src="/trust/verify-qr.svg" alt="" className="trust-reg__qr-img" aria-hidden="true" />
                <span className="trust-reg__qr-caption">{doc.reg.qrCaption}</span>
              </a>
              <a className="trust-reg__verify-btn" href={doc.reg.verifyUrl} target="_blank" rel="noopener noreferrer">
                {doc.reg.verifyLabel}<Arrow />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ── Assurance badges ─────────────────────────────────── */}
      <div className="mkt-container">
        <section className="trust-badges" aria-label={doc.a11y.badges}>
          {doc.badges.map((b) => (
            <div key={b.title} className="trust-badge">
              <span className="trust-badge__icon" aria-hidden="true"><Icon name={b.icon} /></span>
              <div>
                <p className="trust-badge__title">{b.title}</p>
                <p className="trust-badge__line">{b.line}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* ── Tabbed pillars ───────────────────────────────────── */}
      <div className="mkt-container trust-body">
        <h2 className="trust-section-title"><BrandTitle text={doc.pillarsTitle} brand={doc.brand} /></h2>

        <div className="trust-tabwrap">
          <div className="trust-tabs" role="tablist" aria-label={doc.pillarsTitle} ref={listRef} onKeyDown={onKey}>
            {doc.pillars.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                id={`tab-${p.id}`}
                aria-selected={i === active}
                aria-controls={`panel-${p.id}`}
                tabIndex={i === active ? 0 : -1}
                className={`trust-tab${i === active ? ' is-active' : ''}`}
                onClick={() => setActive(i)}
              >
                <span className="trust-tab__icon" aria-hidden="true"><Icon name={p.icon} /></span>
                <span className="trust-tab__label"><PriWord word={p.word} pri={p.pri} /></span>
              </button>
            ))}
            <span className="trust-tabs__indicator" style={indicator} aria-hidden="true" />
          </div>

          <Panel p={doc.pillars[active]!} />
        </div>

        {/* FAQ */}
        <section id="faq" className="trust-faq" aria-label={doc.faqTitle}>
          <h2 className="trust-section-title">{doc.faqTitle}</h2>
          <div className="trust-faq__list">
            {doc.faq.map((f, i) => (
              <details key={i} className="trust-faq__item">
                <summary className="trust-faq__q">{f.q}<span className="trust-faq__chevron" aria-hidden="true"><Chevron /></span></summary>
                <p className="trust-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <nav className="trust-related" aria-label={doc.relatedTitle}>
          <p className="trust-related__title">{doc.relatedTitle}</p>
          <div className="trust-related__links">
            {doc.related.map((r) => (
              <LocaleLink key={r.to} to={r.to} className="trust-related__link">{r.label}<Arrow /></LocaleLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
